'use client'

import { useEffect, useState } from 'react'

interface SuccessNotificationProps {
  message: string
  onClose?: () => void
  duration?: number
}

export default function SuccessNotification({
  message,
  onClose,
  duration = 5000
}: SuccessNotificationProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [isExiting, setIsExiting] = useState(false)

  useEffect(() => {
    // Trigger entrance animation
    setTimeout(() => setIsVisible(true), 10)

    // Start exit animation before closing
    const exitTimer = setTimeout(() => {
      setIsExiting(true)
    }, duration - 300)

    // Close notification
    const closeTimer = setTimeout(() => {
      if (onClose) onClose()
    }, duration)

    return () => {
      clearTimeout(exitTimer)
      clearTimeout(closeTimer)
    }
  }, [duration, onClose])

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-6 transition-all duration-300 ${
        isVisible && !isExiting ? 'opacity-100' : 'opacity-0'
      }`}
      style={{ pointerEvents: isExiting ? 'none' : 'auto' }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300"
        onClick={() => {
          setIsExiting(true)
          setTimeout(() => {
            if (onClose) onClose()
          }, 300)
        }}
      />

      {/* Success Card */}
      <div
        className={`relative max-w-md w-full transition-all duration-500 ease-out ${
          isVisible && !isExiting
            ? 'scale-100 translate-y-0 opacity-100'
            : 'scale-95 translate-y-4 opacity-0'
        }`}
      >
        {/* Glow effect */}
        <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/30 via-accent-primary/30 to-cyan-500/30 rounded-lg opacity-75 blur-xl animate-pulse"></div>

        {/* Main content */}
        <div className="relative bg-bg-primary border border-accent-primary/30 rounded-lg p-8 shadow-2xl">
          {/* Success icon */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              {/* Animated rings */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 rounded-full border-2 border-accent-primary/30 animate-ping"></div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-16 h-16 rounded-full border-2 border-accent-primary/50 animate-pulse"></div>
              </div>

              {/* Icon container */}
              <div className="relative w-16 h-16 bg-accent-primary/20 rounded-full flex items-center justify-center border border-accent-primary/50">
                {/* Checkmark */}
                <svg
                  className="w-8 h-8 text-accent-primary"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  style={{
                    animation: 'checkmark 0.6s ease-out forwards',
                    strokeDasharray: 50,
                    strokeDashoffset: 50
                  }}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2.5}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>
          </div>

          {/* Success message */}
          <div className="text-center space-y-3">
            <h3 className="text-xl font-light text-text-primary">
              Message Received
            </h3>
            <p className="text-sm text-text-secondary leading-relaxed">
              {message}
            </p>
          </div>

          {/* Progress bar */}
          <div className="mt-6 h-1 bg-bg-elevated rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-accent-primary to-cyan-400 rounded-full"
              style={{
                animation: `progress ${duration}ms linear forwards`
              }}
            ></div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes checkmark {
          to {
            stroke-dashoffset: 0;
          }
        }
        @keyframes progress {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>
  )
}
